import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { from, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { Utils } from 'src/app/shared/utils/utils';
import { AuthService } from '../../services/auth.service';

const URLS_TO_SKIP = [
  '/login',
  '/exchangeKeys',
  '/haloDotGoUrl',
  '/getMerchantDocuments',
  '/register/JWT',
  '/generateAccessToken',
  '/accessToken',
];

export const jwtInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const logger = inject(NGXLogger);

  logger.debug(`HTTP[${req.method}] Request ${req.url}`, req.body || '');

  if (shouldSkipValidation(req)) {
    return next(req);
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const accessToken = authService.accessToken;
  const refreshToken = authService.refreshToken;

  // Sign out if the refresh token is expired
  if (refreshToken && isTokenExpired(refreshToken, nowInSeconds)) {
    logger.debug('Refresh token has expired');
    authService.signOut();
  }

  // Handle access token logic
  if (accessToken) {
    if (isTokenExpired(accessToken, nowInSeconds)) {
      return handleExpiredAccessToken(req, next, authService, logger);
    }
    return next(attachAuthHeader(req, accessToken));
  }

  return next(req);
};

/**
 * Safely extracts the expiration timestamp (in seconds) from a JWT payload.
 */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

/**
 * Checks if a JWT token is expired given the current Unix timestamp in seconds.
 */
function isTokenExpired(token: string, nowInSeconds: number): boolean {
  const expiry = getTokenExpiry(token);
  return expiry !== null && nowInSeconds >= expiry;
}

/**
 * Determines whether the request should skip JWT validation.
 */
function shouldSkipValidation(req: HttpRequest<unknown>): boolean {
  const isSkippedUrl = URLS_TO_SKIP.some((url) => req.url.endsWith(url));
  return (!Utils.isExternalUrl(req) && isSkippedUrl) || req.withCredentials;
}

/**
 * Clones the HTTP request and attaches the Bearer Authorization header.
 */
function attachAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Handles the asynchronous token refresh flow when the access token has expired.
 */
function handleExpiredAccessToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  logger: NGXLogger,
): Observable<HttpEvent<unknown>> {
  logger.debug('Access token has expired, attempting refresh');

  return from(authService.tryRefresh()).pipe(
    concatMap((refreshedSuccessfully) => {
      if (refreshedSuccessfully && authService.accessToken) {
        const authorizedReq = attachAuthHeader(req, authService.accessToken);
        return next(authorizedReq);
      }
      authService.signOut();
      return next(req);
    }),
  );
}
